import $ from 'jquery';

import 'datatables.net-bs4';
import 'datatables.net-responsive';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';

import { module as gui } from '../controller/gui.js';
import { module as utilSpinner } from '../utils/waitspinner.js';
import { module as notificaton } from '../components/notification.js';

import '../../styles/components/table.scss';

/** @class Table representing a table. */
class Table {
    /**
     * Creates an instance of Table.
     *
     * @constructor
     * @author: Cäcilia Röhl, Kristin Rohleder
     * @param { HTMLElement } element to be initalised as datatable
     * @param {object (json)} config contains the configuration for initalising the component datatable
     *
     * This class renders the given element as a datatable
     */
    constructor(element, config) {
        this.config = config; // configuration: type of table, name of components, ...
        this.options = config.options; // options for displaying the table: orderable, searchable, ...
        this.element = element;
        this.$element = $(this.element);

        /**
         * Creates the final option object to initalise datatable with
         *
         * @return {object (json)} initalisation config for datatable
         */
        this.getInitOptions = () => {
            const table = this;
            return gui.extendObjects(Table.datatableOptions(table), this.options);
        };
    }

    /**
     * Creates the option object to initalise datatable with
     * this contains general options such as: responsive, what language, don't show pagination if only 1 page exists, ...
     *
     * @return {object (json)} initalisation config for datatable
     */
    static datatableOptions = (table) => {
        const tableInitOptions = {
            retrieve: true,
            responsive: {
                details: {
                    type: 'column',
                    target: 'tr'
                }
            },
            renderer: 'bootstrap',
            language: Table.getLanguageSetting(),
            drawCallback: function() {
                const pagination = $(this).closest('.dataTables_wrapper').find('.dataTables_paginate');
                pagination.toggle(this.api().page.info().pages > 1);
            },
            // shows an informativ tooltip on the responsive "more information"-button in first column,
            // everytime a new row is created (also after ajax-reload)
            createdRow: function(row) {
                Table.setInformationTooltip(table.$element.DataTable(), row, false);
            }
        };

        if (table.options.scrollable) {
            tableInitOptions.scrollY = '50vh';
            tableInitOptions.scrollCollapse = true;
        }

        return tableInitOptions;
    };

    /**
     * Gets the language file for displaying the datatable table
     *
     * @return {object} language file for datatables
     */
    static getLanguageSetting() {
        return require('i18n/datatables/' + Table.getLanguage() + '.lang');
    }

    /**
     * Gets the language name to fetch the language datatables file for the current system language
     *
     * @return {string} language name for the current system language
     */
    static getLanguage() {
        const language = gui.getLocaleLanguage();
        const langmatrix = {
            'de_DE': 'German',
            'de': 'German',
            'en_GB': 'English',
            'en_US': 'English',
            'en': 'English',
            'us': 'English',
            'fr': 'French',
            'ru': 'Russian'
        };

        if ( Object.prototype.hasOwnProperty.call(langmatrix, language)) {
            return langmatrix[ language ];
        } else {
            return 'English'; // default language
        }
    }

    /**
     * set configuration of datatable
     */
    static setupDataTable() {
        // don't alert or throw an error --> see events for own error-handling
        $.fn.dataTable.ext.errMode = 'none';

    }

    /**
     * Configure the initial table-element before initialise datatable
     */
    initTable() {
        // add a new column at the beginning of a row to show the moreIcon for responsive-option
        this.$element.find('tr').prepend(function() {
            return (this.parentElement.nodeName === 'THEAD') ? '<th></th>' : '<td></td>';
        });
    }

    static setInformationTooltip(dataTable, row, showHide) {
        const tooltip = (showHide)
            ? dataTable.i18n('lessInformation', 'show less information')
            : dataTable.i18n('moreInformation', 'show more information');
        $(row).find('.control').attr('title', tooltip);
    }

    /**
     * Events that we need for our datatable
     */
    tableEvents() {
        const table = this;

        // Use the event as a custom error handler to show errors in console and notificaton for user
        table.$element.on('error.dt', function(e, settings, techNote, message) {
            gui.debug('techNote: ' + techNote + '\r\n' +
                'message: ' + message);
            // cut of message for user-friendly output
            const splitedMessage = message.split('- ');
            splitedMessage.splice(0, 1);
            const errorMessage = splitedMessage.join('- ');
            notificaton.showNotification(errorMessage, 'error');
        }).DataTable();

        // shows an informativ tooltip on the responsive "more information"-button in first column
        table.$element.on('responsive-display.dt', function(e, datatable, row, showHide) {
            Table.setInformationTooltip(datatable, row.node(), showHide);
        });
    }

    /**
     * initialises the table as datatable with the options
     */
    init() {
        this.initTable();
        this.$element.DataTable(this.getInitOptions());
        Table.setupDataTable();
        this.tableEvents();
    }

}

/**
 * Creates an instance of TableJson.
 * This slass extends the class Table
 *
 * @constructor
 * @author: Cäcilia Röhl, Kristin Rohleder
 *
 * This class initialises the given element as a datatable with server requests to fetch the data
 */
class TableJson extends Table {
    constructor(element, config) {
        super(element, config);
        this.config = config;
        this.options = config.options;
        this.datatable = '';
        this.datatableId = '';
        this.delayTimer = 500;

        this.responseList = this.config.data.source.response.list; // request name to fetch the data for this table
        this.deferLoading = this.config.data.deferLoading; // how many entries are in this table

        this.getDataSourceResponseList = () => this.responseList;
        this.getDeferLoading = () => this.deferLoading;

        /**
         * Creates the options to initalise the datatable as a server side processed table
         *
         * @return {object} option for the initialisation of an ajax data table
         */
        this.datatableJsonOptions = () => {
            const table = this;
            return {
                'processing': true,
                'serverSide': true,
                'ajax': {
                    url: table.getDataSourceResponseList(),
                    error: function(jqXHR, textStatus, errorThrown) {
                        gui.error('Status returned: ' + jqXHR.statusText + '\r\n' +
                            'textStatus: ' + textStatus + '\r\n' +
                            'errorThrown: ' + errorThrown);
                        notificaton.showNotification(`
                            An error occurred while transmitting the data.
                            Please try again. Or contact your administrator.`,
                        'error');
                    }
                },
                'deferLoading': table.getDeferLoading()
            };
        };

        /**
         * Creates the final options to initalise the datatable
         *
         * @return {object} option for the initialisation of the table as a datatable
         */
        this.getInitOptions = () => {
            const table = this;

            let options = gui.extendObjects(Table.datatableOptions(table), this.datatableJsonOptions());
            options = gui.extendObjects(options, this.options);
            return options;
        };
    }

    static delay(callback, ms, ...args) {
        let timer = 0;
        return function() {
            const context = this;
            clearTimeout(timer);
            timer = setTimeout(function() {
                callback.apply(context, args);
            }, ms || 0);
        };
    }

    /**
     * initialises the table as datatable with the options
     * and fires events
     */
    init() {
        this.initTable();
        this.datatable = this.$element.DataTable(this.getInitOptions());
        if (this.datatable) {
            this.datatableId = this.datatable.settings()[ 0 ].sTableId;
            Table.setupDataTable();
            this.tableEvents();
            this.tableJsonEvents();
        }
    }

    /**
     * Events that we need for our json filled datatable
     */
    tableJsonEvents() {
        const table = this;

        /**
         * Activates / Deactivates the loading spinner
         */
        table.$element.on('processing.dt', function(e, settings, processing) {
            if (processing) {
                // before the data is fetched by ajax
                utilSpinner.show(true);
            } else {
                // the data was fetched and rendered into the table
                utilSpinner.show(false);
            }
        });

        /**
         * adds a delay when typing in the search field
         * Default behaviour was: Each keystroke fired the processing.dt event and showed the loading spinner
         */
        $('.dataTables_filter input[aria-controls="' + table.datatableId + '"]') // select the correct input field
            .off() // Unbind previous default bindings
            .on('input', (
                TableJson.delay(function() { // Bind our desired behavior
                    table.datatable.search($(this).val()).draw();
                }, table.delayTimer)
            ));
    }
}

/**
 * @param { HTMLElement } element to be initalised as datatable
 * @param {object (json)} config contains the configuration for initalising the component datatable
 *
 * Starts an instance of the class Table or TableJson,
 * depending on the type of the table
 * If the table is of an unknown type, it gives feedback via the console (only in developement mode)
 */
const module = {
    init: function(element, config) {
        let table;

        if (config.data.source.type.toLowerCase() === 'json') {
            table = new TableJson(element, config);
        }

        if (config.data.source.type.toLowerCase() === 'html') {
            table = new Table(element, config);
        }

        if (table) {
            table.init();
        } else {
            // is show only in dev mode on the console
            // in live mode (minified, ...) this console out is not shown
            gui.debug('Unknown datasource type for this table: ');
            gui.debug($(element));
        }
    }
};

export { module };
